import Group from 'admin/components/Group'
import NewGroup from 'admin/components/NewGroup'
import InputField from 'admin/components/shared/InputField'
import useDocument from 'admin/hooks/useDocument'
import useGroups from 'admin/hooks/useGroups'
import invert from 'invert-color'
import React, {Fragment, useState} from 'react'
import {Link, Route, Switch, useRouteMatch} from 'react-router-dom'
import Page from 'admin/components/shared/Page'
import selectableCollection from 'admin/components/hoc/selectableCollection'
import {pluralise} from 'admin/lib/format'
import Dropdown from 'admin/components/shared/Dropdown'
import useOrganisation from '../../hooks/useOrganisation'
import useBoolState from '../../hooks/useBoolState'
import Deletion from '../Deletion'
import useModal from '../../hooks/useModal'
import ModalLink from '../shared/ModalLink'

const Table = ({data, SelectAll, SelectOne, organisationId, onClick}) => {
  return (
    <table className={`table is-striped is-fullwidth is-hoverable is-linked`}>
      <thead>
        <tr>
          <th>
            <SelectAll />
          </th>
          <th>Name</th>
          <th># of accounts</th>
        </tr>
      </thead>
      <tbody>
        {data.map((group, i) => {
          let style = {}
          let color = group.colour || 'f5f5f5'
          style.backgroundColor = `#${color}`
          style.color = invert(`#${color}`, true)

          const handleRowClick = e => {
            do {
              if (e.target.tagName?.toLowerCase() == 'a' || e.target.type == 'checkbox') return
              e.target = e.target.parentNode
            } while (e.target)

            e.preventDefault()
            onClick(group.id)
          }

          return (
            <tr key={i} onClick={handleRowClick}>
              <td>
                <SelectOne identifier={group.id} />
              </td>
              <td>
                <span className='tag' style={style}>
                  {group.name}
                </span>
              </td>
              <td>
                {group.numOfDraftAccounts > 0 && (
                  <span>
                    <Link to={`/orgs/${organisationId}/accounts/draft?includeGroupIds=${group.id}`}>
                      {group.numOfDraftAccounts} Draft
                    </Link>
                    &nbsp;&nbsp;
                  </span>
                )}
                {group.numOfActiveAccounts > 0 && (
                  <span>
                    <Link to={`/orgs/${organisationId}/accounts/active?includeGroupIds=${group.id}`}>
                      {group.numOfActiveAccounts} Active
                    </Link>
                    &nbsp;&nbsp;
                  </span>
                )}
                {group.numOfArchivedAccounts > 0 && (
                  <span>
                    <Link to={`/orgs/${organisationId}/accounts/archived?includeGroupIds=${group.id}`}>
                      {group.numOfArchivedAccounts} Archived
                    </Link>
                    &nbsp;&nbsp;
                  </span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default () => {
  const match = useRouteMatch()
  const {organisationID} = match.params
  const {pushModal} = useModal()
  const {organisation} = useOrganisation(organisationID)
  const {groups, filters, setFilters, destroy} = useGroups(organisationID)
  const [selected, setSelected] = useState([])

  const [showDeleteModal, setShowDeleteModal, toggleShowDeleteModal] = useBoolState()

  useDocument({title: 'Groups'})

  const handleRowClick = groupId => pushModal('group', {type: 'custom', groupId})

  const handleDelete = ({notes}) => {
    destroy({recordIds: selected, notes}, () => {
      setSelected([])
    })
  }

  const handleSearch = changes => {
    setFilters({...changes, page: 1})
    setSelected([])
  }

  const SelectableTable = selectableCollection(Table, {
    onSelect: setSelected,
    selectedIdentifiers: selected
  })

  return (
    <Fragment>
      <Switch>
        <Route exact path={`${match.path}/:groupID(\\d+)`} component={Group} />
        <Route path={`${match.path}/new`} component={NewGroup} />
      </Switch>

      {showDeleteModal && <Deletion onClose={toggleShowDeleteModal} onConfirm={handleDelete} />}

      <Page
        title='Manage custom groups'
        subtitle={groups.length ? 'Showing all custom groups' : 'No custom groups found'}
        buttons={
          <ModalLink modal='new-group' params={{type: 'custom'}} className='button is-light is-decorationless'>
            New group
          </ModalLink>
        }>
        <form className='box is-radiusless is-shadowless has-background-filters'>
          <div className='field is-grouped'>
            <div className='control is-expanded'>
              <InputField
                type='search'
                name='q'
                label='Search'
                autoComplete='off'
                value={filters.q}
                debounce={true}
                onChange={handleSearch}
              />
            </div>
          </div>
        </form>

        {groups.length ? (
          <Fragment>
            {selected.length ? (
              <div className='level'>
                <div className='level-left has-background-info box mb-0 is-shadowless p-3 pl-4'>
                  <div className='level-item has-text-white'>
                    {selected.length === groups.length ? 'All ' : ''}
                    {pluralise('group', selected.length, true)} on this page {pluralise('is', selected.length)} selected
                  </div>
                  <Dropdown id='groups-actions-menu' className='level-item'>
                    <button className='button is-outlined is-small'>
                      <span>Action</span>
                      <span className='icon is-small'>
                        <i className='fa fa-caret-down' aria-hidden='true'></i>
                      </span>
                    </button>
                    {organisation.smsEnabled ? (
                      <ModalLink
                        modal='new-text-message'
                        params={{groupIds: selected.join(','), sender: organisation.externalName}}
                        className='dropdown-item'>
                        Text message
                      </ModalLink>
                    ) : null}
                    <a onClick={toggleShowDeleteModal} className='dropdown-item'>
                      Delete
                    </a>
                  </Dropdown>
                </div>
              </div>
            ) : null}

            <SelectableTable data={groups} organisationId={organisationID} onClick={handleRowClick} />
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
